import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { useTranslation } from "react-i18next";
import useLogout from "hooks/useLogout";
import { STATUS_ACTIVE, SUBSTATUS_READY_FOR_WORK } from "utils/constantValues";
import { canAccessFinancialDetails } from "utils/helper";

// Import sidebar icons
import VendorLogo from "images/sidebar/vendor-logo.svg";
import UserIcon from "images/sidebar/user.svg";
import ProfileTwoUserIcon from "images/sidebar/profile-2user.svg";
import BriefcaseIcon from "images/sidebar/briefcase.svg";
import ClipboardTickIcon from "images/sidebar/clipboard-tick.svg";
import DocumentTextIcon from "images/sidebar/document-text.svg";
import CoinIcon from "images/sidebar/coin.svg";
import ReceiptItemIcon from "images/sidebar/receipt-item.svg";
import CardIcon from "images/sidebar/card.svg";
import ArrowIcon from "images/sidebar/arrow-left.svg";

import LogoutIcon from "images/sidebar/logout.svg";

const Sidebar = ({ show = true, isOnboarding = false }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [t] = useTranslation();

  const user = useSelector((state) => state.user || {});
  const userDetails = useSelector((state) => state.userDetails || {});

  const isAgency = user?.isAgency ?? false;

  const { onLogoutSubmit, isLoading: isLogoutLoading } = useLogout();

  const isActiveUser =
    STATUS_ACTIVE === userDetails?.status ||
    SUBSTATUS_READY_FOR_WORK === userDetails?.subStatus;

  const showFinancialDetails = canAccessFinancialDetails(userDetails);

  const menuItems = [
    {
      id: "details",
      label: isAgency ? t("companyDetails") : t("personalDetails"),
      icon: UserIcon,
      path: "/",
      show: isOnboarding ? false : true,
    },
    {
      id: "team-members",
      label: t("teamMembers"),
      icon: ProfileTwoUserIcon,
      path: "/team-members",
      show: isOnboarding ? false : isAgency && isActiveUser,
    },
    {
      id: "services",
      label: isAgency ? t("servicesRates") : t("skillsAndRates"),
      icon: BriefcaseIcon,
      path: "/services",
      show: isOnboarding ? false : true,
    },
    {
      id: "projects",
      label: isAgency ? t("projects") : t("tasks"),
      icon: ClipboardTickIcon,
      path: "/projects",
      show: isOnboarding ? false : true,
    },
    {
      id: "documents",
      label: t("documents"),
      icon: DocumentTextIcon,
      path: "/documents",
      show: isOnboarding ? false : true,
    },
    // {
    //   id: "earnings",
    //   label: t("earnings"),
    //   icon: CoinIcon,
    //   path: "/earnings",
    //   show:isOnboarding ? false : true,
    // },
    // {
    //   id: "invoices",
    //   label: t("invoices"),
    //   icon: ReceiptItemIcon,
    //   path: "/invoices",
    //   show:isOnboarding ? false : isAgency,
    // },
    {
      id: "financial-details",
      label: t("financialDetails"),
      icon: CardIcon,
      path: "/financial-details",
      show: isOnboarding ? false : showFinancialDetails,
    },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    onLogoutSubmit();
  };

  const toggleSidebar = () => {
    // setIsCollapsed(!isCollapsed);
  };

  if (!show) {
    return null;
  }

  return (
    <div
      className={`relative flex flex-col items-center gap-[12px] flex-shrink-0 self-stretch border-r border-[#E4E4E7] bg-white transition-all duration-300 ${
        isCollapsed ? "w-16" : "w-[236px]"
      }`}
      style={{
        padding: isCollapsed ? "0 4px 12px 4px" : "0 12px 12px 12px",
      }}
    >
      {/* Logo Section */}
      <div
        className="flex items-center self-stretch border-b border-[#E4E4E7]"
        style={{
          padding: isCollapsed ? "24px 0" : "24px 0 24px 12px",
        }}
      >
        {!isCollapsed && (
          <img src={VendorLogo} alt="Vendors Logo" className="h-6" />
        )}
      </div>

      {/* Menu Items */}
      <div className="flex flex-col gap-[8px] w-full flex-1 overflow-y-auto">
        {menuItems
          .filter((item) => item.show)
          .map((item) => {
            const isActive = location.pathname === item.path;

            return (
              <div
                key={item.id}
                onClick={() => handleMenuClick(item.path)}
                className={`flex p-[12px] items-center gap-[10px] cursor-pointer ${
                  isActive ? "bg-[#F3F5F7] rounded-[12px]" : ""
                } ${isCollapsed ? "justify-center" : ""}`}
                title={isCollapsed ? item.label : ""}
              >
                <img
                  src={item.icon}
                  alt={item.label}
                  className="w-[20px] h-[20px]"
                />
                {!isCollapsed && (
                  <span
                    className={`font-medium text-[16px] ${
                      isActive ? "text-primary font-[500]" : "text-[#51525C]"
                    }`}
                  >
                    {item.label}
                  </span>
                )}
              </div>
            );
          })}
      </div>

      {/* Logout Button - fixed at bottom */}
      <div className="sticky bottom-0 left-0 right-0 bg-white w-full">
        <div
          onClick={handleLogout}
          disabled={isLogoutLoading}
          className={`flex p-[12px] gap-[10px] items-center cursor-pointer ${
            isCollapsed ? "justify-center" : ""
          } ${isLogoutLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          title={isCollapsed ? t("logout") : ""}
        >
          <img src={LogoutIcon} alt="Logout" className="w-[20px] h-[20px]" />
          {!isCollapsed && (
            <span className="text-[#D6001D] text-[16px] font-medium">
              {isLogoutLoading ? t("loggingOut") : t("logout")}
            </span>
          )}
        </div>
      </div>

      {/* Floating Toggle Button */}
      {/* <div
        onClick={toggleSidebar}
        className="absolute flex justify-center items-center gap-[10px] border border-[#E4E4E7] bg-[#F7F8F8] rounded-[6px] p-[6px] w-[32px] h-[32px] cursor-pointer"
        style={{
          right: "-16px",
          bottom: "56px",
        }}
      >
        <div className="flex justify-center items-center">
          <img
            src={ArrowIcon}
            alt="Arrow"
            className={`transform transition-transform duration-300 ${
              isCollapsed ? "rotate-180" : ""
            }`}
          />
           <div className="text-[20px] mt-[2.5px]">
            {isCollapsed ? ">" : "<"}
          </div> 
        </div>
      </div> */}
    </div>
  );
};

export default Sidebar;
